/**
 * Master Key Management for Zero-Knowledge Notes Encryption
 *
 * The master key is used to encrypt therapy notes. It is:
 * - Generated once during onboarding
 * - Encrypted with the user's password (for normal access)
 * - Also encrypted with a recovery key (for account recovery)
 * - NEVER sent to the server in plaintext
 */

const PBKDF2_ITERATIONS = 100000;
const SALT_LENGTH = 16;
const KEY_LENGTH = 256;
const IV_LENGTH = 12;

/**
 * Generate a cryptographically secure master key
 */
export async function generateMasterKey(): Promise<Uint8Array> {
  const key = new Uint8Array(32); // 256 bits
  crypto.getRandomValues(key);
  return key;
}

/**
 * Generate a random salt for key derivation
 */
export function generateSalt(): Uint8Array {
  const salt = new Uint8Array(SALT_LENGTH);
  crypto.getRandomValues(salt);
  return salt;
}

/**
 * Derive an encryption key from a password using PBKDF2
 */
async function deriveKeyFromPassword(
  password: string,
  salt: Uint8Array
): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const passwordBuffer = encoder.encode(password);

  // Import password as key material
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    passwordBuffer.buffer as ArrayBuffer,
    'PBKDF2',
    false,
    ['deriveKey']
  );

  // Derive AES key from password
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt.buffer as ArrayBuffer,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Import a raw key (from recovery key bytes) as a CryptoKey
 */
async function importRawKey(keyBytes: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'raw',
    keyBytes.buffer as ArrayBuffer,
    { name: 'AES-GCM', length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypt data using AES-GCM
 */
async function encryptWithKey(
  data: Uint8Array,
  key: CryptoKey
): Promise<{ ciphertext: Uint8Array; iv: Uint8Array }> {
  const iv = new Uint8Array(IV_LENGTH);
  crypto.getRandomValues(iv);

  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv.buffer as ArrayBuffer },
    key,
    data.buffer as ArrayBuffer
  );

  return {
    ciphertext: new Uint8Array(ciphertext),
    iv,
  };
}

/**
 * Decrypt data using AES-GCM
 */
async function decryptWithKey(
  ciphertext: Uint8Array,
  iv: Uint8Array,
  key: CryptoKey
): Promise<Uint8Array> {
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv.buffer as ArrayBuffer },
    key,
    ciphertext.buffer as ArrayBuffer
  );

  return new Uint8Array(plaintext);
}

/**
 * Encrypt the master key with the user's password
 * Returns a combined blob: salt (16) + iv (12) + ciphertext
 */
export async function encryptMasterKeyWithPassword(
  masterKey: Uint8Array,
  password: string,
  salt?: Uint8Array
): Promise<Uint8Array> {
  const actualSalt = salt || generateSalt();
  const derivedKey = await deriveKeyFromPassword(password, actualSalt);
  const { ciphertext, iv } = await encryptWithKey(masterKey, derivedKey);

  // Combine: salt + iv + ciphertext
  const combined = new Uint8Array(actualSalt.length + iv.length + ciphertext.length);
  combined.set(actualSalt, 0);
  combined.set(iv, actualSalt.length);
  combined.set(ciphertext, actualSalt.length + iv.length);

  return combined;
}

/**
 * Decrypt the master key using the user's password
 * Input is combined blob: salt (16) + iv (12) + ciphertext
 */
export async function decryptMasterKeyWithPassword(
  encryptedBlob: Uint8Array,
  password: string
): Promise<Uint8Array> {
  const salt = encryptedBlob.slice(0, SALT_LENGTH);
  const iv = encryptedBlob.slice(SALT_LENGTH, SALT_LENGTH + IV_LENGTH);
  const ciphertext = encryptedBlob.slice(SALT_LENGTH + IV_LENGTH);

  const derivedKey = await deriveKeyFromPassword(password, salt);
  return decryptWithKey(ciphertext, iv, derivedKey);
}

/**
 * Encrypt the master key with the recovery key
 * Recovery key should be 32 bytes (256 bits)
 * Returns: iv (12) + ciphertext
 */
export async function encryptMasterKeyWithRecoveryKey(
  masterKey: Uint8Array,
  recoveryKeyBytes: Uint8Array
): Promise<Uint8Array> {
  const cryptoKey = await importRawKey(recoveryKeyBytes);
  const { ciphertext, iv } = await encryptWithKey(masterKey, cryptoKey);

  // Combine: iv + ciphertext
  const combined = new Uint8Array(iv.length + ciphertext.length);
  combined.set(iv, 0);
  combined.set(ciphertext, iv.length);

  return combined;
}

/**
 * Decrypt the master key using the recovery key
 * Input is: iv (12) + ciphertext
 */
export async function decryptMasterKeyWithRecoveryKey(
  encryptedBlob: Uint8Array,
  recoveryKeyBytes: Uint8Array
): Promise<Uint8Array> {
  const iv = encryptedBlob.slice(0, IV_LENGTH);
  const ciphertext = encryptedBlob.slice(IV_LENGTH);

  const cryptoKey = await importRawKey(recoveryKeyBytes);
  return decryptWithKey(ciphertext, iv, cryptoKey);
}

/**
 * Extract the salt from a password-encrypted blob
 */
export function extractSaltFromEncryptedBlob(encryptedBlob: Uint8Array): Uint8Array {
  return encryptedBlob.slice(0, SALT_LENGTH);
}

/**
 * Convert Uint8Array to base64 for storage/transmission
 */
export function bytesToBase64(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes));
}

/**
 * Convert base64 to Uint8Array
 */
export function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
